/**
 * Campaign relationship field utilities.
 *
 * SAFRS 3.x rejects jsonapi-vuex relationship objects in POST payloads.
 * These must be stripped before creating a campaign — only _id foreign
 * key fields are needed for association resolution.
 *
 * IMPORTANT: This list must mirror all relationship fields on the backend
 * Campaign model (api/src/models/campaign.py). If a new relationship is
 * added to Campaign, add it here too. To verify, check the model's
 * db.relationship() declarations.
 *
 * See: #307, #308, PR #254 (same pattern in product creation)
 */
/**
 * The SAFRS collection endpoint a NEW store is POSTed to.
 *
 * The Campaign model was renamed to Store (PR-5a) and the SAFRS surface
 * flipped to /api/stores/ (PR-5b). A 308 redirect keeps /api/campaigns
 * working for GETs, but browsers refuse to follow a redirect on a CORS
 * *preflight* (the OPTIONS preceding a JSON:API POST) -- so posting a new
 * store to /campaigns dies with "Redirect is not allowed for a preflight
 * request" (regression #1868, from #1831). Always POST to the real
 * collection so no redirect is involved. Single source of truth so the
 * store-create wire contract is testable without mounting the SFC.
 */
export const STORE_CREATE_ENDPOINT = '/stores';

export const CAMPAIGN_RELATIONSHIP_FIELDS = [
  'accounts',
  'campaign_products',
  // PR E: ``stores.departments`` JSON column was dropped. getCampaignById
  // no longer returns it, so stripping is now a defensive no-op; kept so a
  // stale cached payload carrying the legacy key never reaches a POST.
  'departments',
  'managers',
  'distributor',
  'has_orders',       // relationship to Order model, not a boolean flag
  'locations',
];

/**
 * Strip relationship fields from a campaign object before POST.
 * Returns a shallow copy with relationship fields removed.
 * Preserves all _id foreign key fields and non-relationship attributes.
 */
export function stripRelationshipFields(campaign) {
  const result = { ...campaign };
  CAMPAIGN_RELATIONSHIP_FIELDS.forEach(key => delete result[key]);
  return result;
}

/**
 * Client Portal v2 (PR C): the 0 / 1 / N storefront department-picker rule.
 *
 * A store maps to zero-or-more client departments (the typed
 * ``client_departments`` array surfaced by getCampaignById). The checkout
 * picker behaves per the build contract
 * (``docs/planning/2026-06-30-client-portal-v2-data-api-refactor.md``
 * section 2 / PR C):
 *
 *   - 0 mapped departments -> 'none'     (no prompt, no attribution).
 *   - 1 mapped department  -> 'auto'     (auto-attributed server-side; the
 *                                          shopper is NOT asked).
 *   - 2+ mapped departments -> 'required' (the shopper MUST pick).
 *
 * Pure: takes the typed department array (or anything array-like / nullish)
 * and returns one of the three mode strings. Single source of truth so the
 * picker's visibility (CartCheckout) and the place-order gate
 * (StorefrontCart) cannot disagree.
 *
 * @param {Array<{id:number,name:string}>|null|undefined} clientDepartments
 * @returns {'none'|'auto'|'required'}
 */
export function departmentPickerMode(clientDepartments) {
  const list = Array.isArray(clientDepartments) ? clientDepartments : [];
  if (list.length === 0) return 'none';
  if (list.length === 1) return 'auto';
  return 'required';
}

/**
 * Whether the STORE, not the shopper, decides where an order ships.
 *
 * True for the `company` and `department` shipping modes
 * (`stores.shipping_mode`, served on the storefront store read). There the
 * shopper enters no address: the server resolves the destination
 * (`checkout_quote` -> `ship_to`), and checkout quotes tax against it. Any
 * other value -- `shopper_address`, or a payload without the field -- is a
 * shopper-entered store, the server's own reading of an unknown mode.
 *
 * @param {{shipping_mode?: string}|null|undefined} campaign
 * @returns {boolean}
 */
export function storeResolvesDestination(campaign) {
  const mode = campaign && campaign.shipping_mode;
  return mode === 'company' || mode === 'department';
}

/**
 * The deferred-tender payment method: the shopper is billed LATER rather
 * than paying at the moment of purchase, which is what requires a GATED
 * store.
 *
 * Note this is the ONE value named here, rather than a pair with the card
 * method. "Accept Shopper Payments" is not a separate column -- that toggle
 * IS ``stores.payment_method``, and switching it off CLEARS the value -- so
 * the column carries three meaningfully different states: the card string,
 * this payroll string, and EMPTY (shopper payments off).
 *
 * At CHECKOUT the backend collapses the last two, because "collects
 * nothing" and "bills later" have the same consequence at the moment of
 * purchase. At CONFIGURATION time they are different CHOICES, and only this
 * one is the dead end -- so this module tests against it directly rather
 * than negating the card string.
 */
export const PAYROLL_PAYMENT_METHOD = 'Invoice / Payroll Deductions';

/**
 * Access modes gated well enough for the backend to accept deferred FUNDING
 * (payroll / allowance / client budget).
 *
 * Mirrors ``deferred_tender.PERMITTED_ACCESS_MODES``. Net Checkout does not
 * use it: since MVP-6 Net is offered on the store's Net PIN, whatever the
 * access mode (``Store.net_checkout_available``). An ALLOWLIST, matching
 * the backend's own posture: an unknown or missing mode is not gated, which
 * is the fail-closed answer.
 */
export const GATED_ACCESS_MODES = ['email_domain', 'password'];

/**
 * Is this store configured so that NO shopper can ever complete checkout?
 *
 * The dead end: payroll tender (the shopper pays nothing at purchase)
 * combined with an ungated store. The backend refuses deferred tender unless
 * the store is gated, and the fallback then demands a card -- on a store
 * configured never to show a card field. The shopper is stuck with no action
 * available to them, and the fix is only available to the store's owner.
 *
 * Pure, and the single source of truth for the editor warning, so the two
 * pages that own the two halves of the conflict (Store Payment Options owns
 * the payment method, Store Login owns the access mode) cannot disagree
 * about whether a given configuration is broken. Same reasoning as
 * ``departmentPickerMode`` above.
 *
 * The backend enforces this independently at save time -- this helper is the
 * editor's early warning, NOT the control.
 *
 * @param {{payment_method?: string, access_mode?: string}|null|undefined} campaign
 * @returns {boolean}
 */
export function isUnsellablePaymentConfiguration(campaign) {
  if (!campaign) return false;
  // Keyed on the PAYROLL VALUE, not on "anything that is not the card
  // string" -- matching the backend guard
  // (``Store._reject_unsellable_payment_configuration``) exactly.
  //
  // Two values that are NOT payroll must not warn, for different reasons:
  //
  //   - EMPTY is how "Accept Shopper Payments = OFF" is persisted (the
  //     toggle has no column of its own). Such a store collects nothing
  //     from the shopper, so no credit is extended and the gating rule has
  //     nothing to bite on.
  //   - ABSENT is a store still being created. Warning there would fire on
  //     every new store before the owner has done anything wrong, which
  //     trains people to ignore the warning.
  if (campaign.payment_method !== PAYROLL_PAYMENT_METHOD) return false;
  return !GATED_ACCESS_MODES.includes(campaign.access_mode);
}
